import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const EMPTY = { name: "", email: "", order_number: "", subject: "", message: "", website: "" };
const input = "w-full rounded-xl border border-[#D8CDBC] bg-white px-4 py-3 text-sm text-[#5A433B] placeholder:text-[#5E4D5A]/60 focus:outline-none focus:border-[#59394F] focus:ring-2 focus:ring-[#A8B59A]/40";

const Field = ({ label, optional, children }) => (
  <label className="block">
    <span className="text-sm text-[#5A433B]">{label}{optional && <span className="text-[#5E4D5A]"> (optional)</span>}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", { ...form, order_number: form.order_number || null });
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(err.response?.status === 429 ? err.response.data.detail : "We couldn't send your message. Please check the fields and try again.");
    }
    setSending(false);
  };

  if (sent) {
    return (
      <div className="rounded-2xl bg-white/80 border border-[#A8B59A] p-8 text-center" data-testid="contact-success">
        <CheckCircle2 className="mx-auto text-[#5E6B51]" size={36} strokeWidth={1.5} />
        <p className="font-serif text-2xl mt-3">Thank you! Your message has been sent.</p>
        <p className="text-sm text-[#5E4D5A] mt-2">We'll get back to you as soon as we can.</p>
        <button onClick={() => setSent(false)} className="mt-5 text-sm text-[#59394F] underline underline-offset-4" data-testid="contact-send-another">Send another message</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl bg-white/80 border border-[#D8CDBC] p-6 sm:p-8 space-y-5" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Name"><input required maxLength={100} value={form.name} onChange={set("name")} className={input} autoComplete="name" data-testid="contact-name" /></Field>
        <Field label="Email address"><input required type="email" maxLength={254} value={form.email} onChange={set("email")} className={input} autoComplete="email" data-testid="contact-email" /></Field>
      </div>
      <Field label="Order number" optional><input maxLength={50} value={form.order_number} onChange={set("order_number")} className={input} data-testid="contact-order" /></Field>
      <Field label="Subject"><input required maxLength={150} value={form.subject} onChange={set("subject")} className={input} data-testid="contact-subject" /></Field>
      <Field label="Message"><textarea required maxLength={5000} rows={6} value={form.message} onChange={set("message")} className={`${input} resize-y`} data-testid="contact-message" /></Field>
      <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      <button type="submit" disabled={sending} className="w-full sm:w-auto bg-[#59394F] text-[#F4EBDD] rounded-full px-8 py-3 text-sm tracking-wide hover:bg-[#432A3B] transition-colors disabled:opacity-60" data-testid="contact-submit">
        {sending ? "Sending…" : "Submit"}
      </button>
    </form>
  );
};
